import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Users, Briefcase, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import useReveal from "../hooks/useReveal";
import { VEHICLES, vehicleModels } from "../data/vehicles";
import { useLang } from "../i18n/LanguageContext";

export default function FleetSlider() {
  useReveal();
  const navigate = useNavigate();
  const { t } = useLang();
  const [idx, setIdx] = useState(0);
  const total = VEHICLES.length;

  const go = useCallback((n) => setIdx((n + total) % total), [total]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 4500);
    return () => clearInterval(t);
  }, [total]);

  const v = VEHICLES[idx];

  return (
    <section id="fleet" className="py-20 md:py-28 bg-black text-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl reveal">
          <span className="text-xs uppercase tracking-[0.2em] text-[#F5B400] font-semibold">{t("fleet_eyebrow")}</span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold leading-tight">{t("fleet_title")}</h2>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative reveal">
            <div className="rounded-3xl overflow-hidden bg-gradient-to-b from-white/10 to-white/5 border border-white/10">
              <img key={v.id} src={v.image} alt={v.name} loading="lazy" decoding="async" width="800" height="533" className="w-full h-[300px] md:h-[380px] object-contain p-6" data-testid="fleet-slide-image" />
            </div>
            <button onClick={() => go(idx - 1)} aria-label="Zurück" data-testid="fleet-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white text-black flex items-center justify-center hover:bg-[#F5B400] hover:text-neutral-900 transition-colors shadow-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => go(idx + 1)} aria-label="Weiter" data-testid="fleet-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white text-black flex items-center justify-center hover:bg-[#F5B400] hover:text-neutral-900 transition-colors shadow-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="reveal">
            <h3 className="font-display text-3xl font-bold" data-testid="fleet-slide-name">{v.name}</h3>
            <p className="mt-2 text-slate-300 text-lg">{vehicleModels(v, t)}</p>
            <div className="mt-6 flex items-center gap-8 text-slate-200">
              <span className="flex items-center gap-2"><Users className="w-5 h-5 text-[#F5B400]" /> {v.pax} {t("fleet_pax")}</span>
              <span className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-[#F5B400]" /> {v.bags} {t("fleet_bags")}</span>
            </div>
            <Button onClick={() => navigate("/booking")} className="mt-8 bg-[#F5B400] hover:bg-[#D99A00] text-neutral-900 rounded-full px-8 h-12 font-semibold" data-testid="fleet-book">
              {t("fleet_book")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <div className="mt-8 flex flex-wrap gap-2">
              {VEHICLES.map((veh, i) => (
                <button key={veh.id} onClick={() => go(i)} data-testid={`fleet-dot-${veh.id}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${i === idx ? "bg-[#F5B400] text-neutral-900" : "bg-white/10 text-slate-300 hover:bg-white/20"}`}>
                  {veh.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
